/**
 * Bugsnap control: opens the in-app bug report dialog when enabled for the tenant.
 *
 * Rendered inside page chrome (the global AppHeader and the local top bars /
 * toolbars of full-screen canvas/session routes that hide the AppHeader) so the
 * action is reachable from the header of every page. Self-gates on tenant config,
 * so it is safe to mount in multiple toolbars — at most one is on screen per page.
 */

import { Bug } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useBugReportEnabled } from './useBugReportEnabled'
import { useBugReportModal } from './BugReportCaptureProvider'
import { BUGSNAP_UI_PROPS } from './bugsnapUi'
import { useShieldedTrigger } from './useShieldedTrigger'
import './BugReportButton.css'

interface BugReportButtonProps {
  /** Icon-only rendering for dense toolbars (no "Bugsnap" text label). */
  compact?: boolean
  /** Extra class appended to the trigger button for per-toolbar theming. */
  className?: string
}

export function BugReportButton({ compact = false, className }: BugReportButtonProps = {}) {
  const { currentUser } = useAuth()
  const { enabled, loading } = useBugReportEnabled()
  const { openModal } = useBugReportModal()

  // Shielded so opening the dialog does not dismiss whatever the user is reporting
  // (open row menus, dropdowns, popovers) — see ./useShieldedTrigger.
  const triggerRef = useShieldedTrigger<HTMLButtonElement>(openModal)

  if (loading || !enabled || !currentUser) {
    return null
  }

  const buttonClassName = [
    'bug-report-header-btn',
    compact ? 'bug-report-header-btn--compact' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      ref={triggerRef}
      type="button"
      className={buttonClassName}
      // Fallback only: the shield normally swallows the click before React sees it.
      onClick={openModal}
      title="Bugsnap"
      aria-label="Bugsnap"
      {...BUGSNAP_UI_PROPS}
    >
      <Bug size={16} aria-hidden />
      {!compact && <span className="bug-report-header-btn__label">Bugsnap</span>}
    </button>
  )
}

